import { ReactNode } from "react";
import { FormField, useRestApiProvider } from "@jugl-web/rest-api";
import { Alert } from "@jugl-web/ui-components";
import {
  convertSnakeCaseToReadableString,
  getImagePathForSharedLibrary,
  useToast,
  useTranslations,
} from "@jugl-web/utils";
import warningGif from "./assets/warning.gif";

export const ConfirmDeleteFieldAlert = ({
  isOpen,
  field,
  entityId,
  onClose,
  onSuccess,
}: {
  isOpen: boolean;
  field: FormField;
  entityId: string;
  onClose: () => void;
  onSuccess?: () => void;
}) => {
  const { t } = useTranslations();
  const { toast } = useToast();
  const { customersFormFieldApi } = useRestApiProvider();

  const [deleteField] = customersFormFieldApi.useDeleteFormFieldMutation();
  const imgSrc = getImagePathForSharedLibrary(warningGif);

  const handleDeleteField = async () => {
    const response = await deleteField({ entityId, fieldId: field.id });

    if ("data" in response) {
      toast(
        t({
          id: "customers-page.custom-field-deleted-successfully",
          defaultMessage: "Custom field deleted successfully",
        })
      );
      onClose();
      onSuccess?.();
    }
  };

  return (
    <Alert
      isOpen={isOpen}
      onRequestClose={onClose}
      img={<img src={imgSrc} alt="Warning" className="h-[70px] w-[70px]" />}
      className="w-[448px]"
      content={
        <div className="px-4 text-center text-dark-700">
          {t(
            {
              id: "customers-page.delete-custom-field-description",
              defaultMessage:
                "Are you sure you want to delete custom field <text>{name}</text>? It will be removed from all customer profiles",
            },
            {
              name: convertSnakeCaseToReadableString(field?.name),
              text: (chunks: ReactNode) => (
                <div
                  className="max-w-[450px] truncate font-medium text-primary"
                  title={convertSnakeCaseToReadableString(field?.name)}
                >
                  {chunks}
                </div>
              ),
            }
          )}
        </div>
      }
      title={t({
        id: "customers-page.delete-custom-field",
        defaultMessage: "Delete Custom Field",
      })}
      buttonsContainerClassName="flex justify-center [&>button]:flex-none"
      buttons={[
        {
          text: t({ id: "common.cancel", defaultMessage: "Cancel" }),
          role: "close",
          className:
            "font-normal font-secondary font-medium text-dark-700 w-[180px] h-10",
        },
        {
          text: t({
            id: "common.delete",
            defaultMessage: "Delete",
          }),
          className: "font-normal font-secondary font-medium w-[180px] h-10",
          color: "tertiary",
          onClick: (_, actions) => {
            handleDeleteField();
            actions.closeAlert();
          },
        },
      ]}
    />
  );
};
